// useLaneColumnSizing.js
//
// Column resize for Board Lanes: field-type defaults + view.columnWidths persistence.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { updateView } from 'services/views/ViewWriteService';

import {
  buildColumnWidthPayload,
  extractViewColumnWidths,
  getAllowedWidthKeys,
  mergeEffectiveColumnWidths,
  normalizeColumnWidths,
} from 'views/table/utils/tableColumnUtils';
import { buildViewSyncPayload } from 'views/table/utils/tableViewSync';
import { canPersistViewLayout } from 'views/utils/organicViews';
import {
  getLaneDefaultColumnWidth,
  getLaneMinColumnWidth,
} from 'views/board/utils/laneColumnWidths';

/**
 * useLaneColumnSizing
 *
 * @param {object} params
 * @param {object|null} params.view
 * @param {string[]} params.visibleFieldKeys
 * @param {(key: string) => object|null} [params.resolveFieldDef]
 * @returns {{ effectiveColumnWidths: Record<string, number>, handleColumnResizeStart: Function }}
 */
const useLaneColumnSizing = ({ view, visibleFieldKeys, resolveFieldDef }) => {
  const viewColumnWidths = useMemo(() => (
    extractViewColumnWidths(view?.columnWidths)
  ), [view?.columnWidths]);

  const allowedWidthKeys = useMemo(() => (
    getAllowedWidthKeys(visibleFieldKeys)
  ), [visibleFieldKeys]);

  const normalizedColumnWidths = useMemo(() => (
    normalizeColumnWidths(viewColumnWidths, allowedWidthKeys)
  ), [viewColumnWidths, allowedWidthKeys]);

  const [columnWidthOverrides, setColumnWidthOverrides] = useState({});
  const columnWidthOverridesRef = useRef({});
  const resizeStateRef = useRef(null);

  const defaultColumnWidths = useMemo(() => {
    const next = {};
    (visibleFieldKeys || []).forEach((key) => {
      const fieldDef = typeof resolveFieldDef === 'function' ? resolveFieldDef(key) : null;
      next[key] = getLaneDefaultColumnWidth(key, fieldDef);
    });
    return next;
  }, [resolveFieldDef, visibleFieldKeys]);

  const effectiveColumnWidths = useMemo(() => {
    const merged = mergeEffectiveColumnWidths(
      normalizedColumnWidths,
      columnWidthOverrides,
      allowedWidthKeys,
    );
    const next = { ...defaultColumnWidths };
    Object.entries(merged).forEach(([key, value]) => {
      if (allowedWidthKeys.has(key) && typeof value === 'number' && value > 0) {
        next[key] = value;
      }
    });
    return next;
  }, [
    allowedWidthKeys,
    columnWidthOverrides,
    defaultColumnWidths,
    normalizedColumnWidths,
  ]);

  useEffect(() => {
    if (!canPersistViewLayout(view)) return;

    const { payload, widthsChanged } = buildViewSyncPayload({
      viewColumnWidths,
      normalizedColumnWidths,
    });

    if (!widthsChanged) return;

    updateView(view.key, payload).catch((error) => {
      console.error('[BoardLanes] sync column widths error:', error);
    });
  }, [view, viewColumnWidths, normalizedColumnWidths]);

  useEffect(() => {
    setColumnWidthOverrides((prev) => {
      if (!prev || typeof prev !== 'object') return {};
      return Object.entries(prev).reduce((acc, [key, value]) => {
        if (allowedWidthKeys.has(key) && typeof value === 'number' && value > 0) {
          acc[key] = value;
        }
        return acc;
      }, {});
    });
  }, [allowedWidthKeys]);

  useEffect(() => {
    columnWidthOverridesRef.current = columnWidthOverrides || {};
  }, [columnWidthOverrides]);

  const handleColumnResizeStart = useCallback((event, key) => {
    if (!key) return;
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = effectiveColumnWidths[key]
      || defaultColumnWidths[key]
      || getLaneDefaultColumnWidth(key, null);
    const minWidth = getLaneMinColumnWidth(key);

    resizeStateRef.current = {
      key,
      startX,
      startWidth,
      minWidth,
    };

    const handleMouseMove = (moveEvent) => {
      if (!resizeStateRef.current) return;
      const {
        key: activeKey,
        startX: originX,
        startWidth: originWidth,
        minWidth: floor,
      } = resizeStateRef.current;
      const delta = moveEvent.clientX - originX;
      const nextWidth = Math.max(floor, Math.round(originWidth + delta));
      resizeStateRef.current.currentWidth = nextWidth;
      setColumnWidthOverrides((prev) => ({
        ...prev,
        [activeKey]: nextWidth,
      }));
    };

    const handleMouseUp = () => {
      const active = resizeStateRef.current;
      resizeStateRef.current = null;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (!active || !canPersistViewLayout(view)) return;

      const { key: activeKey, startWidth: baseWidth, currentWidth } = active;
      const finalWidth = currentWidth
        ?? effectiveColumnWidths[activeKey]
        ?? baseWidth;

      const latestOverrides = columnWidthOverridesRef.current || {};
      const nextWidths = normalizeColumnWidths({
        ...normalizedColumnWidths,
        ...latestOverrides,
        [activeKey]: finalWidth,
      }, allowedWidthKeys);

      updateView(view.key, buildColumnWidthPayload(nextWidths)).catch((error) => {
        console.error('[BoardLanes] update column width error:', error);
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [
    allowedWidthKeys,
    defaultColumnWidths,
    effectiveColumnWidths,
    normalizedColumnWidths,
    view,
  ]);

  return {
    effectiveColumnWidths,
    handleColumnResizeStart,
  };
};

export default useLaneColumnSizing;
